/**
 * Tile grid. `blocked[i]` is 1 when a unit cannot enter the tile (forest, building, or
 * anything else solid). `lumber[i]` is the lumber left on a forest tile; a forest tile
 * whose lumber reaches 0 becomes open ground.
 */
export interface GameMap {
  readonly width: number;
  readonly height: number;
  readonly blocked: Uint8Array;
  readonly lumber: Uint16Array;
}

export function createMap(width: number, height: number): GameMap {
  return {
    width,
    height,
    blocked: new Uint8Array(width * height),
    lumber: new Uint16Array(width * height),
  };
}

export function inBounds(map: GameMap, tx: number, ty: number): boolean {
  return tx >= 0 && ty >= 0 && tx < map.width && ty < map.height;
}

export function isBlocked(map: GameMap, tx: number, ty: number): boolean {
  if (!inBounds(map, tx, ty)) return true;
  return map.blocked[ty * map.width + tx] === 1;
}

export function setBlocked(map: GameMap, tx: number, ty: number, blocked: boolean): void {
  if (!inBounds(map, tx, ty)) throw new Error(`setBlocked: (${tx},${ty}) is outside the map`);
  map.blocked[ty * map.width + tx] = blocked ? 1 : 0;
}

/** True when the tile is standing forest (has lumber left). */
export function isForest(map: GameMap, tx: number, ty: number): boolean {
  if (!inBounds(map, tx, ty)) return false;
  return (map.lumber[ty * map.width + tx] ?? 0) > 0;
}

export function lumberAt(map: GameMap, tx: number, ty: number): number {
  if (!inBounds(map, tx, ty)) return 0;
  return map.lumber[ty * map.width + tx] ?? 0;
}

/** Plant forest on a tile: blocks it and sets its lumber. */
export function setForest(map: GameMap, tx: number, ty: number, lumber: number): void {
  if (!inBounds(map, tx, ty)) throw new Error(`setForest: (${tx},${ty}) is outside the map`);
  if (lumber <= 0 || lumber > 65535)
    throw new Error(`setForest: lumber must be 1..65535, got ${lumber}`);
  const i = ty * map.width + tx;
  map.lumber[i] = lumber;
  map.blocked[i] = 1;
}

/**
 * Take up to `amount` lumber from a forest tile. Returns what was taken. When the tile
 * runs out it becomes open ground, which pathfinding sees immediately.
 */
export function takeLumber(map: GameMap, tx: number, ty: number, amount: number): number {
  if (!inBounds(map, tx, ty)) return 0;
  const i = ty * map.width + tx;
  const have = map.lumber[i] ?? 0;
  const taken = Math.min(have, amount);
  map.lumber[i] = have - taken;
  if (have - taken === 0 && have > 0) map.blocked[i] = 0;
  return taken;
}

/** True when the tile is forest and touches at least one open tile (a worker can stand there). */
export function isEdgeForest(map: GameMap, tx: number, ty: number): boolean {
  if (!isForest(map, tx, ty)) return false;
  for (let dy = -1; dy <= 1; dy++) {
    for (let dx = -1; dx <= 1; dx++) {
      if ((dx !== 0 || dy !== 0) && !isBlocked(map, tx + dx, ty + dy)) return true;
    }
  }
  return false;
}

/**
 * Nearest edge forest tile to (cx, cy) within `radius`, searched ring by ring in a
 * fixed scan order so the choice is deterministic. Null when none.
 */
export function findNearestEdgeForest(
  map: GameMap,
  cx: number,
  cy: number,
  radius: number,
): { x: number; y: number } | null {
  for (let ring = 0; ring <= radius; ring++) {
    for (let ty = cy - ring; ty <= cy + ring; ty++) {
      for (let tx = cx - ring; tx <= cx + ring; tx++) {
        const onRing = Math.max(Math.abs(tx - cx), Math.abs(ty - cy)) === ring;
        if (onRing && isEdgeForest(map, tx, ty)) return { x: tx, y: ty };
      }
    }
  }
  return null;
}
